/* eslint-disable react/no-multi-comp */
import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { useLocation, useParams } from 'react-router';
import { Box, Grid, Typography } from '@mui/material';
import { useRouteScenario } from 'src/hooks/useRouteScenario';
import { SuggestionsProvider } from 'src/hooks/useSuggestionsContext';
import { makeRouteScenario } from 'src/utils/route';
import { MerchantTitle } from 'app/MerchantTitle';
import { useLocationHelpers } from 'components/location/hooks/useLocationHelpers';
import { NotWorkingHour } from 'components/location/NotWorkingHour';
import { OrderingDisabled } from 'components/location/OrderingDisabled';
import { OrderToTableLocationPicker } from 'components/location/OrderToTableLocationPicker';
import { ServiceNotAvailable } from 'components/location/ServiceNotAvailable';
import { MenuDesktop } from 'components/menu/Desktop/MenuDesktop';
import { MenuDesktopSkeleton } from 'components/menu/Desktop/MenuDesktopSkeleton';
import { MenuCategoryGroupsList } from 'components/menu/MenuCategoryGroupsList';
import { MenuList } from 'components/menu/MenuList';
import { MenuSkeleton } from 'components/menu/MenuSkeleton';
import { MenuViewBasketButton } from 'components/menu/MenuViewBasketButton';
import { filterCategoryGroups, ICategoryGroup } from 'components/menu/model/Menu';
import { OrderScenario } from 'components/order/model/Order';
import { TimeslotPicker } from 'components/timeslots/picker/TimeslotPicker';
import { useTimeSlotHelpers } from 'components/timeslots/useTimeSlotHelpers';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { ResponsiveLayout } from 'lib/Responsive';
import { Throbber } from 'lib/Throbber';
import { useLocalHistory } from 'lib/useLocalHistory';
import { resetGuestState } from 'store/guest/guestSlice';
import { getMenu } from 'store/menu/menuSlice';
import { getRecentOrders } from 'store/orders/ordersSlice';
import { loadTimeslots } from 'store/settings/settingsSlice';
import { ApplicationState } from 'store/store';
import { LocationRouteParams, ROUTES } from './routes';
import { LocationState } from 'components/location/model/Location';
import { useQuery } from 'src/hooks/useQuery';
import { isDefined, isEmptyString } from 'lib/typeInference';
import { ConfirmAgeDialog } from 'components/order/modals/ConfirmAgeDialog';
import { useConfirmAgeDialog } from 'components/order/hooks/useConfirmAgeDialog';
import { MenuDeliveryInfo } from 'components/delivery/MenuDeliveryInfo';
import { useDeliveryState } from 'components/delivery/hooks/useDeliveryState';
import { DineInPrepTimeCard } from 'components/timeslots/picker/DineInTimeslotCard';
import { setDeliveryLocation } from 'store/basket/basketSlice';
import { getDeliveryAddressTitle } from 'components/delivery/helpers';
import { MenuAllergenButton } from 'components/menu/MenuAllergenInfoButton';
import { MenuAwardsButton, SHOW_MENU_AWARDS_BUTTON } from 'components/menu/MenuAwardsButton';
import { useAuth } from 'lib/useAuth';
import { useBooleanState } from 'lib/useBooleanState';
import { ViewAwardsDialog } from 'components/awards/ViewAwardsDialog';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';

const PREFIX = 'MenuPage';

const classes = {
    pageContainer: `${PREFIX}pageContainer`
};

const StyledBox = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    [`& .${classes.pageContainer}`]: {
        width: '100%',
        height: '100%',
        display: 'flex',
        overflowY: 'hidden',
        flexDirection: 'column'
    }
});

export const MenuPage: React.FC = () => {
    const { t } = useTranslation();
    const { locationId } = useParams<LocationRouteParams>();
    const { state: routeState } = useLocation();

    const [scenario] = useRouteScenario();
    const dispatch = useAppDispatch();
    const { isTimeSlotsAvailable, asapEnabled } = useTimeSlotHelpers();
    const { isWorkingHour, isLocationOrderEnabled, checkServiceAvailability } = useLocationHelpers();
    const [selectedCategoryGroup, setSelectedCategoryGroup] = React.useState<ICategoryGroup>();
    const {
        open: showAgeDialog,
        handleAgeVerificationConfirm,
        handleAgeVerificationCancel
    } = useConfirmAgeDialog();
    const {
        isLoading: isMenuLoading,
        menu,
        categories
    } = useSelector((state: ApplicationState) => state.menu);
    const { logUserEvent } = useGAHelpers();
    const [isAwardsOpen, showAwards, hideAwards] = useBooleanState();
    const { isGuest } = useAuth();
    const queryTableNumber = useQuery('tableNumber');
    const orderId = useQuery('orderId');
    const { deliveryLocation, timeSlot } = useSelector((state: ApplicationState) => state.basket);
    const settings = useSelector((state: ApplicationState) => state.settings.settings);
    const { push, replace } = useLocalHistory();

    const { isCurrentLocationLoading, currentLocation } = useSelector(
        (state: ApplicationState) => state.locations
    );
    const { recentOrders } = useSelector((state: ApplicationState) => state.orders);

    const scenarioToUse = React.useMemo(() => scenario ?? OrderScenario.PREORDER, [scenario]);

    React.useEffect(() => {
        if (!scenario) {
            push(ROUTES.MENU, { scenario: makeRouteScenario(OrderScenario.PREORDER) });
        }
    }, [push, scenario]);

    React.useEffect(() => {
        if (
            currentLocation &&
            (currentLocation.state === LocationState.RETIRED ||
                currentLocation.state === LocationState.PENDING)
        ) {
            replace(ROUTES.BASE);
        }
    }, [currentLocation, replace]);

    React.useEffect(() => {
        if (isDefined(queryTableNumber) && !isEmptyString(queryTableNumber)) {
            dispatch(setDeliveryLocation(queryTableNumber));
        }
    }, [queryTableNumber, dispatch]);

    const tableNumber = React.useMemo(() => {
        if (isDefined(deliveryLocation) && !isEmptyString(deliveryLocation)) {
            return deliveryLocation;
        }
        if (isDefined(queryTableNumber) && !isEmptyString(queryTableNumber)) {
            return queryTableNumber;
        }
    }, [deliveryLocation, queryTableNumber]);

    React.useEffect(() => {
        dispatch(
            getMenu({
                locationId: locationId || '',
                date: timeSlot?.start,
                scenario: scenarioToUse,
                basketChangeMessage: t('DIALOG_TIMESLOT_CHANGED_MESSAGE'),
                deliveryLocation: tableNumber
            })
        );
    }, [dispatch, locationId, scenarioToUse, timeSlot?.start, t, tableNumber]);

    React.useEffect(() => {
        dispatch(resetGuestState());
    }, [dispatch]);

    const { deliveryTimeMins, selectedAddress } = useDeliveryState();

    const isLoading = React.useMemo(() => isMenuLoading || !settings, [isMenuLoading, settings]);

    React.useEffect(() => {
        if (!!menu && !isLoading && !!scenarioToUse) {
            dispatch(
                getRecentOrders({ menu, options: { scenario: scenarioToUse, locationId, isOpen: false } })
            );
        }
    }, [dispatch, locationId, menu, scenarioToUse, isLoading]);

    const orderAhead = React.useMemo(
        () =>
            (scenarioToUse === OrderScenario.PREORDER || scenarioToUse === OrderScenario.DINE_IN) &&
            (settings?.asapOrderingEnabled || settings?.timeSlotsEnabled),
        [scenarioToUse, settings?.asapOrderingEnabled, settings?.timeSlotsEnabled]
    );
    React.useEffect(() => {
        if (orderAhead) {
            dispatch(
                loadTimeslots({
                    locationId: locationId || '',
                    timeslot: timeSlot,
                    timeslotDoesntExistMessage: t('DIALOG_PREORDER_TIMESLOT_NOT_AVAILABLE')
                })
            );
        }
    }, [dispatch, locationId, orderAhead, t, timeSlot]);

    const categoryGroupsEnabled = React.useMemo(
        () => !!settings && !!settings.app && !!settings.app.showCategoryGroups,
        [settings]
    );

    const validCategoryGroupsToShow = React.useMemo(
        () =>
            categoryGroupsEnabled &&
            !!menu &&
            !!menu.categories &&
            !!menu.categoryGroups &&
            filterCategoryGroups(Object.values(menu.categoryGroups), menu.categories).sort(
                (a, b) => a.sort - b.sort
            ),
        [menu, categoryGroupsEnabled]
    );

    const hasCategoryGroups = React.useMemo(
        () => !!validCategoryGroupsToShow && validCategoryGroupsToShow.length > 1,
        [validCategoryGroupsToShow]
    );

    const noValidCategoryGroups = React.useMemo(
        () =>
            categoryGroupsEnabled &&
            (validCategoryGroupsToShow ? validCategoryGroupsToShow.length === 0 : true),
        [categoryGroupsEnabled, validCategoryGroupsToShow]
    );

    const recentOrdersToShow = React.useMemo(() => {
        if (!!recentOrders && !!settings && !!settings.app && settings.app.showRecentOrders !== false) {
            if (hasCategoryGroups) {
                return recentOrders.slice(0, 10);
            }
            return recentOrders.slice(0, 3);
        }
    }, [hasCategoryGroups, recentOrders, settings]);

    const categoriesToShow = React.useMemo(() => {
        if (hasCategoryGroups) {
            if (!!selectedCategoryGroup) {
                return categories.filter(category => selectedCategoryGroup.categoryIds.includes(category.id));
            }
        } else if (!!validCategoryGroupsToShow && validCategoryGroupsToShow.length === 1) {
            return categories.filter(category =>
                validCategoryGroupsToShow[0].categoryIds.includes(category.id)
            );
        } else {
            return categories;
        }
    }, [categories, hasCategoryGroups, selectedCategoryGroup, validCategoryGroupsToShow]);

    const handleOnBack = React.useCallback(() => {
        if (!!selectedCategoryGroup) {
            setSelectedCategoryGroup(undefined);
        } else if (
            scenarioToUse &&
            (scenarioToUse === OrderScenario.ORDER_TO_TABLE || scenarioToUse === OrderScenario.DINE_IN)
        ) {
            push(ROUTES.JOURNEY.LANDING);
        } else if (scenarioToUse === OrderScenario.TABLE) {
            push(ROUTES.QUICKPAY.CHECKOUT, undefined, `orderId=${orderId}`);
        } else if (scenarioToUse === OrderScenario.PREORDER) {
            const preoderEntry = routeState?.preoderEntry;
            if (preoderEntry === 'journey') {
                push(ROUTES.JOURNEY.LANDING);
                return;
            }
            push(ROUTES.BASE);
        } else {
            push(ROUTES.BASE);
        }
    }, [push, scenarioToUse, selectedCategoryGroup, orderId, routeState]);
    const title = React.useMemo(() => {
        if (scenarioToUse === OrderScenario.TABLE) {
            return t('OTT_TITLE');
        }

        return currentLocation ? currentLocation.title : 'Unknown menu';
    }, [currentLocation, scenarioToUse, t]);

    const handleCategoryGroupClick = React.useCallback((categoryGroup: ICategoryGroup) => {
        setSelectedCategoryGroup(categoryGroup);
    }, []);

    const shouldShowTablePicker = React.useMemo(
        () =>
            !!menu &&
            (scenarioToUse === OrderScenario.ORDER_TO_TABLE || scenarioToUse === OrderScenario.TABLE) &&
            checkServiceAvailability(scenarioToUse),
        [checkServiceAvailability, menu, scenarioToUse]
    );
    const shouldShowDeliveryInfo = React.useMemo(
        () => !!menu && scenario === OrderScenario.DELIVERY,
        [menu, scenario]
    );
    const showPreorderTimeslotPicker = React.useMemo(
        () => !!menu && orderAhead && scenarioToUse === OrderScenario.PREORDER,
        [menu, orderAhead, scenarioToUse]
    );

    const showDineInPrepTime = React.useMemo(
        () => !!menu && orderAhead && scenarioToUse === OrderScenario.DINE_IN,
        [menu, orderAhead, scenarioToUse]
    );

    const showNotWorkingHour = React.useMemo(
        () =>
            !isWorkingHour ||
            (scenarioToUse === OrderScenario.PREORDER && !asapEnabled && !isTimeSlotsAvailable),
        [asapEnabled, isTimeSlotsAvailable, isWorkingHour, scenarioToUse]
    );

    const handleTableChange = React.useCallback(
        ({ current }: { previous?: string; current: string }) => {
            replace('', {}, `tableNumber=${current}`);
        },
        [replace]
    );

    const showAwardsButton = React.useMemo(() => !isGuest && SHOW_MENU_AWARDS_BUTTON, [isGuest]);
    const handleAwardsButtonClick = React.useCallback(() => {
        logUserEvent(EGAEventName.MenuAwardsButtonTapped);
        showAwards();
    }, [logUserEvent, showAwards]);
    const handleCLick = React.useCallback(() => {
        logUserEvent(EGAEventName.MenuAllergensButtonTapped);
    }, [logUserEvent]);

    if (isCurrentLocationLoading) {
        return <Throbber text={t('GENERAL_LOADING')} />;
    }

    if (isLoading) {
        return (
            <ResponsiveLayout
                renderMobileView={<MenuSkeleton />}
                renderDesktopView={<MenuDesktopSkeleton />}
            />
        );
    }

    return (
        <StyledBox className="hidden-scroll">
            <MerchantTitle title={currentLocation?.title} />
            <MuiAppBar title={title} onBack={handleOnBack} />
            <ResponsiveLayout
                renderMobileView={
                    <Box className={classes.pageContainer}>
                        <Box display="flex" flexDirection="column" justifyContent="center" width="100%">
                            {showPreorderTimeslotPicker && <TimeslotPicker />}
                            {showDineInPrepTime && <DineInPrepTimeCard />}
                            {shouldShowTablePicker && (
                                <OrderToTableLocationPicker
                                    isDialogOpenInitially={!tableNumber}
                                    onTableChange={handleTableChange}
                                    searchButton
                                />
                            )}
                            {shouldShowDeliveryInfo && (
                                <MenuDeliveryInfo
                                    deliveryTimeMins={deliveryTimeMins}
                                    title={getDeliveryAddressTitle(selectedAddress)}
                                />
                            )}
                            {(settings?.menu?.allergenUrl || showAwardsButton) && (
                                <Grid container spacing={1} sx={{ p: 1 }}>
                                    {settings?.menu?.allergenUrl && (
                                        <Grid size={showAwardsButton ? 6 : 12}>
                                            <MenuAllergenButton
                                                link={settings.menu.allergenUrl}
                                                onClick={handleCLick}
                                            />
                                        </Grid>
                                    )}
                                    {showAwardsButton && (
                                        <Grid size={settings?.menu?.allergenUrl ? 6 : 12}>
                                            <MenuAwardsButton onClick={handleAwardsButtonClick} />
                                        </Grid>
                                    )}
                                </Grid>
                            )}
                        </Box>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            width="100%"
                            height="100%"
                            overflow="hidden"
                        >
                            {!menu || noValidCategoryGroups ? (
                                <Box padding={2}>
                                    <Typography>{t('MENU_EMPTY')}</Typography>
                                </Box>
                            ) : (
                                <>
                                    {!!menu && !isDefined(categoriesToShow) && (
                                        <>
                                            {showNotWorkingHour && (
                                                <NotWorkingHour scenario={scenarioToUse} />
                                            )}
                                            {!isLocationOrderEnabled && <OrderingDisabled />}
                                            <MenuCategoryGroupsList
                                                recentOrders={recentOrdersToShow}
                                                onClick={handleCategoryGroupClick}
                                                categoryGroups={
                                                    !!validCategoryGroupsToShow
                                                        ? validCategoryGroupsToShow
                                                        : undefined
                                                }
                                            />
                                        </>
                                    )}
                                    {!!menu && !!categoriesToShow && (
                                        <SuggestionsProvider>
                                            <MenuList
                                                showNotWorkingHour={showNotWorkingHour}
                                                recentOrders={recentOrdersToShow}
                                                menu={menu}
                                                hasCategoryGroups={hasCategoryGroups}
                                                categories={categoriesToShow}
                                                currency={settings?.region.currencyCode || ''}
                                            />
                                        </SuggestionsProvider>
                                    )}
                                </>
                            )}
                            {!checkServiceAvailability(scenarioToUse) && (
                                <ServiceNotAvailable scenario={scenarioToUse} />
                            )}
                            <MenuViewBasketButton />
                        </Box>
                    </Box>
                }
                renderDesktopView={
                    <SuggestionsProvider>
                        <MenuDesktop
                            showCategoryGroups={hasCategoryGroups}
                            categoryGroups={validCategoryGroupsToShow || []}
                            menu={menu}
                            recentOrders={recentOrdersToShow}
                            orderAhead={!!orderAhead}
                            noValidCategoryGroups={noValidCategoryGroups}
                            deliveryTimeMins={deliveryTimeMins}
                            deliveryAddress={getDeliveryAddressTitle(selectedAddress)}
                        />
                    </SuggestionsProvider>
                }
            />
            <ConfirmAgeDialog
                open={showAgeDialog}
                onClose={handleAgeVerificationCancel}
                onConfirm={handleAgeVerificationConfirm}
            />
            <ViewAwardsDialog
                open={showAwardsButton && isAwardsOpen}
                onClose={hideAwards}
                locationId={locationId}
            />
        </StyledBox>
    );
};
